/**
 * BrandingContext — fetches the global branding config from the backend and
 * applies it to the document (title, favicon, theme-color, manifest link).
 *
 * Anyone using {appName, logoUrl, faviconUrl, themeColor, accentColor, tagline,
 * footerText} can subscribe via useBranding().
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULTS = {
  app_name: 'SplitEasy',
  tagline: 'Track expenses with friends',
  logo_url: '/icon-192.png',
  favicon_url: '/icon-192.png',
  icon_192_url: '/icon-192.png',
  icon_512_url: '/icon-512.png',
  theme_color: '#143D30',
  accent_color: '#E05D3A',
  footer_text: '',
  version: 0,
};

const BrandingContext = createContext({
  branding: DEFAULTS,
  refresh: () => {},
});

function setLink(rel, href) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  // Cache-bust on update so browsers fetch the new asset
  link.href = href;
}

function setMeta(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function applyToDocument(b) {
  // Title
  document.title = `${b.app_name} | ${b.tagline || 'Track expenses with friends'}`;
  // Theme color
  setMeta('theme-color', b.theme_color);
  setMeta('apple-mobile-web-app-title', b.app_name);
  // Favicon (cache-bust by version)
  const v = b.version || Date.now();
  const favHref = `${b.favicon_url}${b.favicon_url.includes('?') ? '&' : '?'}v=${v}`;
  const apiPrefix = (url) => url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url;
  setLink('icon', apiPrefix(favHref));
  setLink('apple-touch-icon', apiPrefix(`${b.icon_192_url}?v=${v}`));
  // Dynamic manifest
  let manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  manifestLink.href = `${BACKEND_URL}/api/manifest.json?v=${v}`;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/branding`, { cache: 'no-store' });
      if (!resp.ok) return;
      const data = await resp.json();
      setBranding(data);
      applyToDocument(data);
    } catch {
      /* keep defaults */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Helper: returns a public, fully-qualified URL for a logo that may be a
 * backend-relative path (e.g. /api/branding/asset/foo.png) or a public path
 * (e.g. /icon-192.png).
 */
export function brandingAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
}
